import classNames from 'classnames';
import { CopyAllButtonByExcelGenerator } from 'components/shared/CopyAllButton/CopyAllButton';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { FormattedValue } from 'components/shared/ReadOnlyGrid/FormattedValue';
import { formatGridToClipboard } from 'components/shared/ReadOnlyGrid/GridUtils';
import type { ReadOnlyGridData } from 'components/shared/ReadOnlyGrid/ReadOnlyGrid';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import type { IntlKey } from 'locales';
import type { AnalyticCategoryActions, WorkspaceAnalyticCategory } from 'models';
import { type ReactNode, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

export interface SensitivitySectionProps {
  children?: ReactNode;
  sectionName: string;
  data: ReadonlyArray<Record<string, number | string | undefined>>;
  precision: number;

  headerKey?: IntlKey;

  copyActionName?: AnalyticCategoryActions[WorkspaceAnalyticCategory];

  className?: string;

  unavailableDataMessageKey?: IntlKey;
}

const br = { br: <br /> };
export function SensitivitySection({
  unavailableDataMessageKey,
  data,
  className,
  precision,
  headerKey,
  children,
  sectionName,
  copyActionName,
}: SensitivitySectionProps) {
  const isInternal = useIsInternalUser();
  const grid = useMemo(
    () =>
      ({
        columns: [
          { name: 'name', alias: sectionName },
          { name: 'value', alias: 'Value' },
        ],
        cells: data,
      }) as ReadOnlyGridData,
    [data, sectionName],
  );
  const generateFormattedGrid = useCallback(() => formatGridToClipboard(grid), [grid]);
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const sectionE2e = useMemo(() => sectionName.toLocaleLowerCase(), [sectionName]);
  if (data === undefined) {
    return <FormattedMessage id={unavailableDataMessageKey} />;
  }

  return (
    <div className={classNames('d-flex flex-column py-1', className)} data-e2e={`${sectionE2e}-card`}>
      {headerKey && (
        <h5 className="card-title  py-2 text-secondary" data-e2e={`${sectionE2e}-card-title`}>
          <FormattedMessage id={headerKey} />
        </h5>
      )}
      {isInternal ? null : (
        <div className="px-4 mb-5">
          <div className="mb-3">
            <FormattedMessage id="quoteDetails.MetricsWarningTitle" />
          </div>
          <FormattedMessage id="quoteDetails.MetricsWarningContent" tagName="p" values={br} />
        </div>
      )}
      <ListGroup className="w-100 mb-3 flex-wrap " e2e={`${sectionE2e}-card-items`}>
        {data.map(({ name, value }, i) => (
          <ListGroupItem
            key={`${name}-${i}`}
            labelKey={`table.header.row.${name}` as IntlKey}
            defaultLabelMessage={name as any}
            e2e={`${sectionE2e}-item`}
            className="ps-0 pe-3 py-1"
          >
            <FormattedValue value={value} precision={precision} e2e={`${sectionE2e}-item-value`} />
          </ListGroupItem>
        ))}
      </ListGroup>
      <div className="d-flex align-self-end">
        {children}
        <CopyAllButtonByExcelGenerator
          className="float-end"
          generateExcelGrid={generateFormattedGrid}
          analyticsCategory={logAnalyticsCategory}
          analyticsAction={copyActionName}
        />
      </div>
    </div>
  );
}

SensitivitySection.displayName = 'Fees';
